import { isMultipleChoice } from './questionScoring';
import { REPORT_QUESTION_TYPE, type MetricMessage, type MetricQuestion, type MetricResponse } from './types';

const CHOICE_QUESTION_TYPES: string[] = [
  REPORT_QUESTION_TYPE.Select,
  REPORT_QUESTION_TYPE.Multiselect,
  REPORT_QUESTION_TYPE.Scale,
];

export const questionSummaryKey = (questionIndex: string, statementIndex?: number): string =>
  JSON.stringify([questionIndex, statementIndex ?? null]);

export interface ChoiceCount {
  index: number;
  label: string;
  count: number;
}

/** Validated choice histogram and distinct channel sets of one question (statement); what metrics are calculated from. */
export interface ChoiceSummaryCounts {
  questionIndex: string;
  statementIndex?: number;
  type: string;
  multipleChoice: boolean;
  validCount: number;
  respondingChannelIds: Set<string>;
  choiceResponseIds: Set<string>[];
  escapeCount: number;
  invalidCount: number;
  choices: ChoiceCount[];
}

export function createChoiceSummary(question: MetricQuestion, statementIndex?: number): ChoiceSummaryCounts {
  return {
    questionIndex: question.index,
    statementIndex,
    type: question.type,
    multipleChoice: isMultipleChoice(question),
    validCount: 0,
    respondingChannelIds: new Set(),
    choiceResponseIds: (question.answersLocal ?? []).map(() => new Set<string>()),
    escapeCount: 0,
    invalidCount: 0,
    choices: (question.answersLocal ?? []).map((label, index) => ({ label, index, count: 0 })),
  };
}

/**
 * Counts one answer of the channel `channelId` into `summary`. Returns the selected option indexes of a valid choice
 * answer; escapes, invalid values and non-choice answers return undefined.
 */
export function countAnswer(
  summary: ChoiceSummaryCounts,
  message: MetricMessage,
  channelId: string | undefined,
): number[] | undefined {
  if (!CHOICE_QUESTION_TYPES.includes(summary.type)) {
    if (message.text?.trim()) {
      summary.validCount++;
      if (channelId) {
        summary.respondingChannelIds.add(channelId);
      }
    }
    return undefined;
  }

  const raw = message.value?.trim() ?? '';
  if (raw === '' || (summary.type === REPORT_QUESTION_TYPE.Scale && /^-\d+$/.test(raw))) {
    summary.escapeCount++;
    return undefined;
  }
  const tokens = raw.split(',').map((value) => value.trim());
  const indexes = [...new Set(tokens.map(Number))];
  if (
    tokens.some((token) => !/^\d+$/.test(token)) ||
    indexes.some((index) => index >= summary.choices.length) ||
    (!summary.multipleChoice && indexes.length !== 1)
  ) {
    summary.invalidCount++;
    return undefined;
  }
  summary.validCount++;
  if (channelId) {
    summary.respondingChannelIds.add(channelId);
  }
  for (const index of indexes) {
    summary.choices[index].count++;
    if (channelId) {
      summary.choiceResponseIds[index].add(channelId);
    }
  }
  return indexes;
}

/**
 * Choice summaries of every answered question (and matrix statement) of `responses`, keyed by `questionSummaryKey`.
 * A message shared by several responses is counted once.
 */
export function summarizeChoiceAnswers(
  questions: readonly MetricQuestion[],
  responses: readonly MetricResponse[],
): Map<string, ChoiceSummaryCounts> {
  const questionsByIndex = new Map(questions.map((question) => [question.index, question]));
  const summaries = new Map<string, ChoiceSummaryCounts>();
  const seen = new Set<string>();
  for (const response of responses) {
    for (const message of response.messages ?? []) {
      if (seen.has(message.id)) {
        continue;
      }
      seen.add(message.id);
      const question = questionsByIndex.get(message.questionDefinitionIndex);
      if (!question) {
        continue;
      }
      const statements = message.statementIndex == null ? [undefined] : [undefined, message.statementIndex];
      for (const statementIndex of statements) {
        const key = questionSummaryKey(question.index, statementIndex);
        const summary = summaries.get(key) ?? createChoiceSummary(question, statementIndex);
        countAnswer(summary, message, String(response.id));
        summaries.set(key, summary);
      }
    }
  }
  return summaries;
}
